import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SalesReturnRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchSalesReturns, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney, formatPosDate } from '@/lib/format';

/**
 * Only 'Refunded' money has actually left the drawer, and it is the only status
 * the sales figures subtract. Showing the others plainly — rather than hiding
 * them — is the point of the page: a pending refund is what someone comes here
 * to find.
 */
function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status.trim().toLowerCase()) {
    case 'refunded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

const FILTER_FIELDS = ['Order', 'Customer', 'Reason', 'Status'] as const;

const TOGGLEABLE = ['branch', 'orderNumber', 'reason', 'status', 'refund'] as const;

const COLUMN_LABELS: Record<string, string> = {
  branch: 'Branch',
  orderNumber: 'Order',
  reason: 'Reason',
  status: 'Status',
  refund: 'Refund',
};

export function SalesReturnsPage() {
  const { currency, filters, multiBranch } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('sales-returns', TOGGLEABLE);

  const returnsQuery = useQuery({
    queryKey: portalKeys.salesReturns(range),
    queryFn: ({ signal }) => fetchSalesReturns(range, signal),
  });

  const report = returnsQuery.data;
  const reportCurrency = report?.currency || currency;

  const rows = useMemo(
    () =>
      applyClientFilters(report?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Order'
            ? row.orderNumber
            : field === 'Customer'
              ? row.customerName
              : field === 'Reason'
                ? row.reason
                : field === 'Status'
                  ? row.status
                  : undefined,
        searchValues: (row) => [row.returnNumber, row.orderNumber, row.customerName, row.reason],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const baseColumns = useMemo<Array<Column<SalesReturnRow>>>(
    () => [
      {
        key: 'returnNumber',
        header: 'Return',
        pinned: true,
        cell: (row) => (
          <div>
            <p className="font-medium text-slate-900">{row.returnNumber}</p>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">
              {formatPosDate(row.date)}
            </p>
          </div>
        ),
      },
      {
        key: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-slate-500">{row.branchName || '—'}</span>,
      },
      {
        key: 'orderNumber',
        header: 'Order',
        // Blank when the parent order is outside the synced window rather than
        // missing — worth a dash instead of an empty cell.
        cell: (row) => <span className="text-slate-500">{row.orderNumber || '—'}</span>,
      },
      {
        key: 'customer',
        header: 'Customer',
        pinned: true,
        cell: (row) => <span className="text-slate-700">{row.customerName || 'Walk-in'}</span>,
      },
      {
        key: 'reason',
        header: 'Reason',
        cell: (row) => <span className="text-slate-500">{row.reason || '—'}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => <Badge tone={statusTone(row.status)}>{row.status || '—'}</Badge>,
      },
      {
        key: 'refund',
        header: 'Refund',
        align: 'right',
        cellClassName: 'tabular-nums font-medium',
        cell: (row) => (
          <span className="text-rose-700">{formatMoney(row.refundAmount, reportCurrency)}</span>
        ),
      },
    ],
    [reportCurrency],
  );

  const columns = useMemo(() => {
    const resolved = applyReportLayout(baseColumns, layout);
    return multiBranch ? resolved : resolved.filter((column) => column.key !== 'branch');
  }, [baseColumns, layout, multiBranch]);

  const header = (
    <PageHeader
      title="Sales returns"
      description="Refunds raised against sales in the selected range."
      actions={
        <ExportMenu
          report="sales-returns"
          range={range}
          disabled={returnsQuery.isPending || returnsQuery.isError}
        />
      }
    />
  );

  if (returnsQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={returnsQuery.error}
            onRetry={() => void returnsQuery.refetch()}
            retrying={returnsQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={returnsQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 2 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile
                label="Refunded"
                value={formatMoney(data.totalRefunded, data.currency || currency)}
                tone="negative"
                hint="Refunded rows only — pending and rejected are excluded"
              />
              <KpiTile label="Returns" value={formatCount(data.count)} />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard
          headingId="sales-returns-table"
          title="Return history"
          count={rows.length}
          description="Newest first. Only refunded rows count towards the total above."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Return, order or customer"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
              placeholderFor={(field) => (field === 'Status' ? 'Refunded / Pending / Rejected' : 'value')}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={returnsQuery}
            skeleton={<TableSkeleton rows={6} columns={6} />}
            isEmpty={() => rows.length === 0}
            emptyTitle={toolbar.search ? `No returns match “${toolbar.search}”` : 'No returns in this range'}
            emptyDescription="Pick a wider date range, or check another branch or till."
          >
            {() => (
              <DataTable
                caption="Sales returns with order, customer, reason, status and refund"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                layout={layout}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
